/**
 * Time control types for game clock
 */

/**
 * Time control preset names.
 *
 * These are the CANONICAL, named cadences: the ones the platform itself offers,
 * in the lobby's quick row and in the arena calendar. Direct challenges are not
 * limited to this list — see `TimeControlKey` below.
 *
 * `blitz-2+5` and `rapid-7+5` arrived last: they are the two daily arenas
 * (`schedule_slots`, migration 082), which ran them as off-table keys for as
 * long as they were the arena's alone. `rapid-7+5` is named because the quick
 * row offers it, and that row only offers cadences on this list.
 *
 * `blitz-2+5` was named for that same reason and then LOST the quick button to
 * the shorter `bullet-2+1` — yet it stays on the list, for two others. It is
 * still the Arena Blitz Italiana's cadence, and a name is what lets a direct
 * challenge sent at 2+5 be remembered at all: `setLastTimeControl` keeps only
 * cadences on this list. (Off-table cadences are still dropped there; that is a
 * wider defect, not one to fix from here.)
 */
export type TimeControlPreset =
  | 'bullet-1+0'
  | 'bullet-1+1'
  | 'bullet-2+0'
  | 'bullet-2+1'
  | 'blitz-2+5'
  | 'blitz-3+0'
  | 'blitz-3+2'
  | 'blitz-5+0'
  | 'blitz-5+3'
  | 'rapid-7+5'
  | 'rapid-10+0'
  | 'rapid-10+5'
  | 'rapid-10+10'
  | 'rapid-15+0'
  | 'rapid-15+10'
  | 'classical-30+0'
  | 'classical-30+20'
  | 'classical-60+0'
  | 'classical-60+30'
  | 'none'; // No time control

/**
 * Rating categories a timed cadence can fall into.
 *
 * The LIST is the declaration and the union derives from it, not the other way
 * round: whoever needs to walk the categories at runtime — bucketing
 * `games.time_control` by its prefix, say — would otherwise write the four
 * strings out again, and those copies already exist (`RATING_TC_CATEGORIES` in
 * game.repository, `TC_CATEGORY_BY_PREFIX` in dashboard.service).
 */
export const TIME_CONTROL_CATEGORIES = ['bullet', 'blitz', 'rapid', 'classical'] as const;
export type TimeControlCategoryName = (typeof TIME_CONTROL_CATEGORIES)[number];

/**
 * Any cadence a game can be played at: `<category>-<minutes>+<increment>`, or
 * `'none'` for an untimed game.
 *
 * Direct challenges let the two players agree on ANY combination of
 * `CHALLENGE_MINUTES` × `CHALLENGE_INCREMENTS`, so the cadence is no longer
 * drawn from a fixed table. The `<category>-` prefix is deliberately kept: it is
 * what `games.time_control` stores, what the `LIKE 'blitz-%'` rating queries
 * match, and what every `split('-')[0]` reader parses. Every `TimeControlPreset`
 * is a valid `TimeControlKey`, so existing rows and stored preferences keep
 * their exact meaning.
 *
 * Never trust a raw key off the wire — run it through `parseTimeControlKey`.
 */
export type TimeControlKey = `${TimeControlCategoryName}-${number}+${number}` | 'none';

/** Minutes offered in the direct-challenge cadence picker (∞ is `'none'`). */
export const CHALLENGE_MINUTES = [1, 2, 3, 5, 7, 10, 15, 20, 30, 45, 60] as const;

/**
 * Per-move increments, in seconds, offered in the direct-challenge picker.
 * A coarse ladder rather than every second from 0 to 60: it covers the useful
 * range without a 61-entry menu, and every canonical preset's increment
 * (0/1/2/3/5/10/20/30) is on it.
 */
export const CHALLENGE_INCREMENTS = [0, 1, 2, 3, 5, 7, 10, 15, 20, 30, 45, 60] as const;

/**
 * Time control configuration
 */
export interface TimeControl {
  /** Initial time in milliseconds */
  initialTime: number;
  /** Increment per move in milliseconds */
  increment: number;
  /** Display name */
  name: string;
  /** Category (bullet, blitz, rapid, classical) */
  category: 'bullet' | 'blitz' | 'rapid' | 'classical' | 'none';
}

/**
 * Clock state for a single player
 */
export interface PlayerClock {
  /** Remaining time in milliseconds */
  timeRemaining: number;
  /** Whether this player's clock is currently running */
  isRunning: boolean;
}

/**
 * Full clock state for the game
 */
export interface ClockState {
  white: PlayerClock;
  black: PlayerClock;
  /** Time control settings */
  timeControl: TimeControl;
  /** Timestamp when the current player's clock started (for accurate timing) */
  lastTickAt: number | null;
}

/**
 * Predefined time control configurations
 */
export const TIME_CONTROL_PRESETS: Record<TimeControlPreset, TimeControl> = {
  'bullet-1+0': {
    initialTime: 1 * 60 * 1000, // 1 minute
    increment: 0,
    name: 'Bullet 1+0',
    category: 'bullet',
  },
  'bullet-1+1': {
    initialTime: 1 * 60 * 1000,
    increment: 1 * 1000, // 1 second
    name: 'Bullet 1+1',
    category: 'bullet',
  },
  'bullet-2+0': {
    initialTime: 2 * 60 * 1000, // 2 minutes
    increment: 0,
    name: 'Bullet 2+0',
    category: 'bullet',
  },
  'bullet-2+1': {
    initialTime: 2 * 60 * 1000,
    increment: 1 * 1000, // 1 second
    name: 'Bullet 2+1',
    category: 'bullet',
  },
  'blitz-2+5': {
    initialTime: 2 * 60 * 1000, // 2 minutes
    increment: 5 * 1000, // 5 seconds
    name: 'Blitz 2+5',
    category: 'blitz',
  },
  'blitz-3+0': {
    initialTime: 3 * 60 * 1000, // 3 minutes
    increment: 0,
    name: 'Blitz 3+0',
    category: 'blitz',
  },
  'blitz-3+2': {
    initialTime: 3 * 60 * 1000,
    increment: 2 * 1000, // 2 seconds
    name: 'Blitz 3+2',
    category: 'blitz',
  },
  'blitz-5+0': {
    initialTime: 5 * 60 * 1000, // 5 minutes
    increment: 0,
    name: 'Blitz 5+0',
    category: 'blitz',
  },
  'blitz-5+3': {
    initialTime: 5 * 60 * 1000,
    increment: 3 * 1000, // 3 seconds
    name: 'Blitz 5+3',
    category: 'blitz',
  },
  'rapid-7+5': {
    initialTime: 7 * 60 * 1000, // 7 minutes
    increment: 5 * 1000, // 5 seconds
    name: 'Rapid 7+5',
    category: 'rapid',
  },
  'rapid-10+0': {
    initialTime: 10 * 60 * 1000, // 10 minutes
    increment: 0,
    name: 'Rapid 10+0',
    category: 'rapid',
  },
  'rapid-10+5': {
    initialTime: 10 * 60 * 1000, // 10 minutes
    increment: 5 * 1000, // 5 seconds
    name: 'Rapid 10+5',
    category: 'rapid',
  },
  'rapid-10+10': {
    initialTime: 10 * 60 * 1000, // 10 minutes
    increment: 10 * 1000, // 10 seconds
    name: 'Rapid 10+10',
    category: 'rapid',
  },
  'rapid-15+0': {
    initialTime: 15 * 60 * 1000, // 15 minutes
    increment: 0,
    name: 'Rapid 15+0',
    category: 'rapid',
  },
  'rapid-15+10': {
    initialTime: 15 * 60 * 1000, // 15 minutes
    increment: 10 * 1000, // 10 seconds
    name: 'Rapid 15+10',
    category: 'rapid',
  },
  'classical-30+0': {
    initialTime: 30 * 60 * 1000, // 30 minutes
    increment: 0,
    name: 'Classical 30+0',
    category: 'classical',
  },
  'classical-30+20': {
    initialTime: 30 * 60 * 1000, // 30 minutes
    increment: 20 * 1000, // 20 seconds
    name: 'Classical 30+20',
    category: 'classical',
  },
  'classical-60+0': {
    initialTime: 60 * 60 * 1000, // 60 minutes
    increment: 0,
    name: 'Classical 60+0',
    category: 'classical',
  },
  'classical-60+30': {
    initialTime: 60 * 60 * 1000, // 60 minutes
    increment: 30 * 1000, // 30 seconds
    name: 'Classical 60+30',
    category: 'classical',
  },
  'none': {
    initialTime: 0,
    increment: 0,
    name: 'No Time Limit',
    category: 'none',
  },
};

// ─── Arbitrary cadences (direct challenge) ───────────────────────────────────

/**
 * Estimated length of a game, in seconds, assuming the 40 moves per side that
 * chess/draughts clocks are conventionally rated on. This — not the base time
 * alone — is what decides the rating category, so a 1+60 game (≈41 minutes)
 * lands in `classical` instead of polluting the `bullet` pool.
 */
export function estimatedDurationSeconds(minutes: number, incrementSeconds: number): number {
  return minutes * 60 + 40 * incrementSeconds;
}

/**
 * Rating category for a cadence, derived from the CLOCK rather than from a name.
 * The thresholds are chosen so that all 19 canonical presets keep exactly the
 * category their name already declares (asserted in the tests).
 */
export function categoryForClock(minutes: number, incrementSeconds: number): TimeControlCategoryName {
  const seconds = estimatedDurationSeconds(minutes, incrementSeconds);
  if (seconds < 180) return 'bullet';
  if (seconds < 480) return 'blitz';
  if (seconds < 1500) return 'rapid';
  return 'classical';
}

/** `(5, 3)` → `'blitz-5+3'`. The one place a cadence key is spelled. */
export function buildTimeControlKey(minutes: number, incrementSeconds: number): TimeControlKey {
  return `${categoryForClock(minutes, incrementSeconds)}-${minutes}+${incrementSeconds}`;
}

const TIME_CONTROL_KEY_RE = /^(bullet|blitz|rapid|classical)-(\d{1,2})\+(\d{1,2})$/;

/**
 * Parse and VALIDATE a cadence key. Returns `null` for anything outside the
 * offered grid — this is the single validator, shared by the challenge UI and
 * the `challenge:send` handler.
 *
 * Note the last check: the key must be spelled exactly as `buildTimeControlKey`
 * would spell it. That rejects both a category contradicting its own clock (a
 * client labelling a 60-minute game `bullet-60+0` to be rated in the bullet
 * pool) and non-canonical spellings (`blitz-05+03`), which would otherwise let
 * one cadence reach `games.time_control` under two different strings.
 *
 * `'none'` is NOT accepted here — it is not a timed cadence. Callers that allow
 * untimed games test for it explicitly (or use `resolveTimeControl`).
 */
export function parseTimeControlKey(
  key: string,
): { minutes: number; increment: number; category: TimeControlCategoryName } | null {
  const match = TIME_CONTROL_KEY_RE.exec(key);
  if (!match) return null;

  const minutes = Number(match[2]);
  const increment = Number(match[3]);

  if (!(CHALLENGE_MINUTES as readonly number[]).includes(minutes)) return null;
  if (!(CHALLENGE_INCREMENTS as readonly number[]).includes(increment)) return null;
  if (buildTimeControlKey(minutes, increment) !== key) return null;

  return { minutes, increment, category: categoryForClock(minutes, increment) };
}

/**
 * Resolve ANY cadence key — canonical preset, custom challenge cadence or
 * `'none'` — to its clock configuration. Returns `null` for an invalid key.
 *
 * Use this instead of indexing `TIME_CONTROL_PRESETS`, which only knows the 19
 * named cadences and throws on `.initialTime` for anything else. For those 19 it
 * reproduces the table entry exactly (asserted in the tests).
 */
export function resolveTimeControl(key: string): TimeControl | null {
  if (key === 'none') return TIME_CONTROL_PRESETS.none;

  const parsed = parseTimeControlKey(key);
  if (!parsed) return null;

  const { minutes, increment, category } = parsed;
  return {
    initialTime: minutes * 60 * 1000,
    increment: increment * 1000,
    name: `${category.charAt(0).toUpperCase()}${category.slice(1)} ${minutes}+${increment}`,
    category,
  };
}

// ─── Low-time warning thresholds ─────────────────────────────────────────────

/**
 * When the "low time" warning fires (ms left), per cadence category.
 *
 * A single fixed threshold cannot serve every cadence: 20s is a third of a
 * bullet 1+0 game (the clock would be red almost from the start) and far too
 * late to be actionable in a classical 60+0. `none` is only a placeholder —
 * an untimed game has no clock to warn about.
 */
export const CLOCK_LOW_TIME_BY_CATEGORY: Record<TimeControl['category'], number> = {
  bullet: 10_000,
  blitz: 20_000,
  rapid: 60_000,
  classical: 60_000,
  none: 20_000,
};

/**
 * Second, closer-to-zero warning. With a 60s threshold the first warning would
 * otherwise be followed by a full minute of silence.
 *
 * Only fires when it is strictly below the cadence threshold, so bullet (whose
 * threshold is exactly this) keeps a single warning.
 */
export const CLOCK_CRITICAL_TIME_MS = 10_000;

/**
 * Low-time threshold (ms) for a cadence key — canonical preset or custom
 * challenge cadence. Falls back to the blitz value for an unknown/missing key,
 * which is the threshold the app used for every cadence before this was
 * category-aware.
 */
export function getClockLowTimeMs(key?: string | null): number {
  if (!key) return CLOCK_LOW_TIME_BY_CATEGORY.blitz;
  const timeControl = resolveTimeControl(key);
  if (!timeControl) return CLOCK_LOW_TIME_BY_CATEGORY.blitz;
  return CLOCK_LOW_TIME_BY_CATEGORY[timeControl.category];
}

/**
 * Hysteresis band for the low-time warning, as a multiple of the threshold and
 * as a number of increments. Both terms earn their place: the multiplier
 * carries the cadence threshold, whose increments are small next to it (a rapid
 * 60s threshold against a 5s increment), and the increment term carries the 10s
 * critical threshold, where a single classical 30s increment would clear any
 * sane multiplier on its own.
 */
export const CLOCK_REARM_FACTOR = 1.5;
export const CLOCK_REARM_INCREMENTS = 3;

/**
 * Clock level (ms) at which a warning that already fired is allowed to fire
 * again — see the constants above for the band.
 *
 * Without a band, Fischer increment turns a one-shot warning into a per-move
 * beep: the increment lands the clock a hair above the threshold the instant
 * you move, the warning re-arms, and the next tick back under it beeps again.
 *
 * The threshold is a parameter rather than derived from the key, so the same
 * rule serves both the cadence warning and the critical one. The key is read
 * only for its increment, so an unknown, missing or untimed key degrades to the
 * increment-free band — the same fallback shape as `getClockLowTimeMs`.
 *
 * Deliberately NOT clamped to the cadence's own initial time. At a cadence like
 * `classical-1+60` the band (240s) sits above the 60s the game starts with, so
 * the warning fires once and then stays quiet — right for a clock that spends
 * the whole game inside its own warning band. Clamping would re-arm at exactly
 * `initialTime`, which a 60s increment clears on every move: the bug again.
 */
export function getClockRearmMs(thresholdMs: number, key?: string | null): number {
  const increment = (key ? resolveTimeControl(key)?.increment : 0) ?? 0;
  return Math.max(
    thresholdMs * CLOCK_REARM_FACTOR,
    thresholdMs + CLOCK_REARM_INCREMENTS * increment
  );
}

/**
 * Quanto si aspetta la PRIMA mossa vera prima di chiudere d'ufficio la partita.
 *
 * Il buco che questi numeri chiudono: `startClock()` arma l'orologio ma lo
 * lascia fermo, e la corsa parte solo alla prima mossa vera. Fino a quel momento
 * nessuna bandierina puo' cadere, quindi un giocatore CONNESSO che non muove
 * teneva la partita viva per sempre. Chi chiude la scheda o perde la rete non
 * passa di qui: il suo scollegamento innesca il periodo di grazia, che la
 * partita la chiude gia'.
 *
 * Perche' due numeri e non uno: quello che cambia non e' la cadenza, e' **quanto
 * c'e' da leggere prima di muovere**. A mossa libera la posizione e' quella di
 * partenza, che si conosce a memoria, e mezzo minuto basta e avanza. Con
 * un'apertura sorteggiata ci si trova davanti a una posizione mai vista, e il
 * tempo per orientarsi deve crescere con l'importanza della partita — cioe' con
 * la cadenza, da cui il terzo del tempo base.
 */
export const SCADENZA_PRIMA_MOSSA_LIBERA_MS = 30_000;
export const SCADENZA_PRIMA_MOSSA_SORTEGGIO_MS = 60_000;

/**
 * Il pavimento d'arena.
 *
 * In arena la scadenza non annulla: fa **perdere a tavolino**. Un annullamento
 * non costa niente a nessuno — nessun rating, nessuna riga a DB — e li' si puo'
 * essere stretti; una sconfitta no. Le arene sorteggiano quasi sempre
 * l'apertura, quindi prendono gia' la formula lunga: questo pavimento serve
 * soltanto all'arena a mossa libera, perche' nemmeno li' si perda in trenta
 * secondi.
 */
export const SCADENZA_PRIMA_MOSSA_ARENA_MIN_MS = 60_000;

/**
 * Millisecondi concessi per la prima mossa vera di una partita.
 *
 * `key` e' letto solo per il tempo base, e una cadenza sconosciuta, mancante o
 * infinita degrada a zero — stessa forma di ripiego di `getClockRearmMs`. A
 * cadenza infinita (`initialTime: 0`) restano quindi i 60 s secchi del
 * sorteggio, che e' la risposta giusta: non c'e' nessun orologio da cui
 * ricavare una proporzione.
 *
 * Sta qui, accanto a `getClockLowTimeMs` e `getClockRearmMs`, perche' e' la
 * stessa specie: una soglia di tempo che dipende dalla cadenza. E sta in
 * `shared` perche' la regola deve essere UNA — il server la applica, e chi
 * volesse spiegarla a schermo legge la stessa funzione.
 */
export function scadenzaPrimaMossaMs(opts: {
  /** L'apertura e' stata sorteggiata (semi-mosse gia' sulla damiera)? */
  aperturaSorteggiata: boolean;
  /** Cadenza della partita; serve solo il tempo base. */
  key?: string | null;
  /** Partita d'arena: si applica il pavimento, perche' li' si perde davvero. */
  arena?: boolean;
}): number {
  const base = opts.aperturaSorteggiata
    ? SCADENZA_PRIMA_MOSSA_SORTEGGIO_MS +
      Math.floor(((opts.key ? resolveTimeControl(opts.key)?.initialTime : 0) ?? 0) / 3)
    : SCADENZA_PRIMA_MOSSA_LIBERA_MS;
  return opts.arena ? Math.max(base, SCADENZA_PRIMA_MOSSA_ARENA_MIN_MS) : base;
}

/**
 * Create initial clock state from a time control preset
 */
export function createClockState(preset: TimeControlKey): ClockState {
  // Falls back to the untimed config so an unparseable key degrades to "no
  // clock" rather than crashing on `undefined.initialTime`.
  const timeControl = resolveTimeControl(preset) ?? TIME_CONTROL_PRESETS.none;
  return {
    white: {
      timeRemaining: timeControl.initialTime,
      isRunning: false,
    },
    black: {
      timeRemaining: timeControl.initialTime,
      isRunning: false,
    },
    timeControl,
    lastTickAt: null,
  };
}

/**
 * Format milliseconds to MM:SS or M:SS display
 */
export function formatTime(ms: number): string {
  if (ms <= 0) return '0:00';

  const totalSeconds = Math.ceil(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;

  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}

/** Below this many ms the clock shows hundredths instead of M:SS. */
export const CLOCK_HUNDREDTHS_THRESHOLD_MS = 10_000;

/**
 * Format remaining clock time for display.
 *
 *   >= 10s -> "M:SS"      < 10s -> "S.hh"      <= 0 -> "0.00"
 *
 * Always floors, and does so with integer arithmetic rather than `toFixed`:
 * the clock must never report more time than the player actually has. The old
 * `toFixed(1)` rounded, so 9960ms rendered as "10.0" — a tenths-branch string
 * that reads as ten full seconds.
 *
 * Both sides of the 10s boundary are 4 characters ("0:10" / "9.99"), so the
 * field never changes width there.
 */
export function formatClockTime(ms: number): string {
  if (!Number.isFinite(ms) || ms <= 0) return '0.00';

  if (ms < CLOCK_HUNDREDTHS_THRESHOLD_MS) {
    const hundredths = Math.floor(ms / 10);
    return `${Math.floor(hundredths / 100)}.${(hundredths % 100).toString().padStart(2, '0')}`;
  }

  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;

  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}

/**
 * Clock sync event sent from server to clients
 */
export interface ClockSyncEvent {
  roomId: string;
  /** Remaining time for white in milliseconds */
  white: number;
  /** Remaining time for black in milliseconds */
  black: number;
  /** Which player's clock is currently running */
  activePlayer: 'white' | 'black' | null;
  /**
   * Server wall-clock timestamp when this event was built. Only meaningful
   * *relative* to another `serverTime`: the client's own clock can be off by
   * minutes, so `Date.now() - serverTime` is not a latency measurement.
   */
  serverTime: number;
}

/**
 * Initial clock state sent when game starts
 */
export interface GameClockInit {
  /** Initial time for white in milliseconds */
  white: number;
  /** Initial time for black in milliseconds */
  black: number;
  /** Increment per move in milliseconds */
  increment: number;
  /** Cadence key used (canonical preset or custom challenge cadence) */
  timeControl: TimeControlKey;
}

/**
 * Server-side clock state for a game room
 */
export interface ServerClockState {
  /** Remaining time for white in milliseconds */
  white: number;
  /** Remaining time for black in milliseconds */
  black: number;
  /** Which player's clock is currently running (null if game not started) */
  activePlayer: 'white' | 'black' | null;
  /** Timestamp when the active player's clock started running */
  lastTickAt: number | null;
  /** Increment per move in milliseconds */
  increment: number;
  /** Whether the clock is running */
  isRunning: boolean;
}
